import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Breadcrumb,
  Checkbox,
  Input,
  Radio,
  Select,
  Spin,
  Table,
  Tooltip,
} from "antd";
import { ReactComponent as RupeesSign } from "../../image/RupeesSign.svg";
import ButtonField from "../../common/htmlComponents/ButtonField";
import { getRequest, postRequest } from "../../axios";
import { authUserData } from "../../utils/Helper";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import {
  buildGeneralPoOrderSubmitPayload,
  getGeneralMaterialRowKey,
  parseGeneralSummaryComparativeResponse,
} from "./generalProcurementPoUtils";

const { Option } = Select;

const REASONS = [
  "Urgent Delivery",
  "Non Availibility of stock",
  "Exploring alternate Supplier",
  "Split of share",
  "Continue Business relation",
  "Assurance of supply",
  "Others",
];

const formatUom = (uom) => (uom === "Cubic Meter" ? "Cu. Mtr." : uom);

const parseComparisonRank = (rank) => {
  const match = String(rank ?? "").match(/\d+/);
  return match ? Number(match[0]) : 999;
};

const sameVendorId = (left, right) =>
  left != null && right != null && String(left) === String(right);

const getMaterialL1VendorId = (material) => {
  const vendorBids = material.vendorBids ?? [];
  const l1ByRanking = vendorBids.find((bid) => bid.comparison_ranking === "L1");
  if (l1ByRanking) return l1ByRanking.vendorId;

  const rankedBids = [...vendorBids].sort(
    (a, b) =>
      parseComparisonRank(a.comparison_ranking) -
        parseComparisonRank(b.comparison_ranking) ||
      (a.total_amount_with_gst || 0) - (b.total_amount_with_gst || 0),
  );
  return rankedBids[0]?.vendorId ?? null;
};

const materialBidRequiresReason = (vendorBid, material) => {
  if (!vendorBid) return false;

  const l1VendorId = getMaterialL1VendorId(material);
  const isLowestByAmount = sameVendorId(vendorBid.vendorId, l1VendorId);

  if (vendorBid.comparison_ranking && vendorBid.comparison_ranking !== "L1") {
    return true;
  }

  return !isLowestByAmount;
};

const findMaterialByRow = (materials, row) =>
  materials.find(
    (item) => getGeneralMaterialRowKey(item) === row.materialKey,
  );

const clearMaterialVendorSelections = (materials) =>
  materials.map((material) => ({
    ...material,
    vendorBids: material.vendorBids.map((vendorBid) => ({
      ...vendorBid,
      vendor_checked: false,
      select_reason: null,
      other_reason_text: "",
    })),
  }));

const GeneralProcurementSummaryPriceComparative = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const CompanyId = authUserData()?.company ?? null;

  const [apiLoading, setApiLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [meta, setMeta] = useState({
    floatId: null,
    company: CompanyId,
    rfqNumber: null,
    poApproval: false,
    otherReason: false,
  });
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [vendorMode, setVendorMode] = useState("common");
  const [selectedVendorTotalAmount, setSelectedVendorTotalAmount] = useState(0);
  const [otherReasons, setOtherReasons] = useState({});
  const [vendorGrandTotals, setVendorGrandTotals] = useState([]);
  const [rawCityDetails, setRawCityDetails] = useState([]);

  useEffect(() => {
    const loadSummary = async () => {
      if (!id) return;
      setApiLoading(true);
      try {
        const apiResponse = await getRequest(
          `general-comparative-rfq/${id}/summery-of-price-comparative/`,
        );

        if (!apiResponse?.data?.data) {
          ErrorNotificationMsg("Failed to load price comparative summary");
          return;
        }

        const parsed = parseGeneralSummaryComparativeResponse(apiResponse.data);
        setMeta({
          floatId: parsed.floatId,
          company: parsed.company ?? CompanyId,
          rfqNumber: parsed.rfqNumber,
          poApproval: parsed.poApproval,
          otherReason: parsed.otherReason,
        });
        setMaterials(parsed.materials);
        setVendorGrandTotals(parsed.vendorGrandTotals ?? []);
        setRawCityDetails(parsed.rawCityDetails ?? []);

        const checkedVendorIds = new Set(
          parsed.materials
            .map((material) =>
              material.vendorBids.find((vendorBid) => vendorBid.vendor_checked),
            )
            .filter(Boolean)
            .map((vendorBid) => vendorBid.vendorId),
        );

        if (parsed.isSameVendor === false) {
          setVendorMode("perItem");
          setSelectedVendorId(null);
        } else if (parsed.isSameVendor === true) {
          setVendorMode("common");
          setSelectedVendorId([...checkedVendorIds][0] ?? null);
        } else if (checkedVendorIds.size > 1) {
          setVendorMode("perItem");
          setSelectedVendorId(null);
        } else {
          setVendorMode("common");
          setSelectedVendorId([...checkedVendorIds][0] ?? null);
        }

        const initialOtherReasons = {};
        parsed.materials.forEach((material) => {
          material.vendorBids.forEach((vendorBid) => {
            if (vendorBid.select_reason === "Others") {
              initialOtherReasons[
                `${vendorBid.vendorId}_${getGeneralMaterialRowKey(material)}`
              ] = vendorBid.other_reason_text || "";
            }
          });
        });
        setOtherReasons(initialOtherReasons);
      } catch (error) {
        console.error("Error loading summary comparative:", error);
        ErrorNotificationMsg("Failed to load price comparative summary");
      } finally {
        setApiLoading(false);
      }
    };

    loadSummary();
  }, [CompanyId, id]);

  useEffect(() => {
    if (vendorMode === "common") {
      if (!selectedVendorId) {
        setSelectedVendorTotalAmount(0);
        return;
      }

      const total = materials.reduce((acc, material) => {
        const vendorBid = material.vendorBids.find((bid) =>
          sameVendorId(bid.vendorId, selectedVendorId),
        );
        return acc + (vendorBid?.total_amount_with_gst || 0);
      }, 0);

      setSelectedVendorTotalAmount(total);
      return;
    }

    const total = materials.reduce((acc, material) => {
      const selectedBid = material.vendorBids.find(
        (vendorBid) => vendorBid.vendor_checked,
      );
      return acc + (selectedBid?.total_amount_with_gst || 0);
    }, 0);

    setSelectedVendorTotalAmount(total);
  }, [materials, selectedVendorId, vendorMode]);

  const vendorNames = useMemo(
    () => [
      ...new Set(
        materials.flatMap((material) =>
          material.vendorBids.map((vendorBid) => vendorBid.vendorName)
        )
      ),
    ],
    [materials]
  );

  const handleVendorModeChange = (nextMode) => {
    setVendorMode(nextMode);
    setSelectedVendorId(null);
    setOtherReasons({});
    setMaterials((prev) => clearMaterialVendorSelections(prev));
    setSelectedVendorTotalAmount(0);
  };

  const handleVendorSelect = (vendorId, isChecked) => {
    if (isChecked) {
      setSelectedVendorId(vendorId);
    } else {
      setSelectedVendorId(null);
    }

    setMaterials((prev) =>
      prev.map((material) => ({
        ...material,
        vendorBids: material.vendorBids.map((vendorBid) => ({
          ...vendorBid,
          vendor_checked:
            sameVendorId(vendorBid.vendorId, vendorId) && isChecked,
          select_reason:
            sameVendorId(vendorBid.vendorId, vendorId) && isChecked
              ? vendorBid.select_reason
              : null,
        })),
      })),
    );
  };

  const handleMaterialVendorSelect = (materialKey, vendorId) => {
    setSelectedVendorId(null);

    setMaterials((prev) =>
      prev.map((material) => {
        if (getGeneralMaterialRowKey(material) !== materialKey) {
          return material;
        }

        return {
          ...material,
          vendorBids: material.vendorBids.map((vendorBid) => ({
            ...vendorBid,
            vendor_checked:
              vendorId != null && sameVendorId(vendorBid.vendorId, vendorId),
            select_reason:
              vendorId != null && sameVendorId(vendorBid.vendorId, vendorId)
                ? vendorBid.select_reason
                : null,
          })),
        };
      }),
    );
  };

  const getSelectedVendorIdForRow = (row) => {
    if (row.rowType !== "material" || vendorMode !== "perItem") {
      return null;
    }

    const material = materials.find(
      (item) => getGeneralMaterialRowKey(item) === row.materialKey,
    );
    return (
      material?.vendorBids.find((vendorBid) => vendorBid.vendor_checked)
        ?.vendorId ?? null
    );
  };

  const renderMaterialVendorSelection = (_, record) => {
    if (record.rowType !== "material") {
      return null;
    }

    const material = findMaterialByRow(materials, record);
    if (!material) {
      return null;
    }

    const selectedBid = material.vendorBids.find(
      (vendorBid) => vendorBid.vendor_checked,
    );

    return (
      <Select
        style={{ width: "100%", minWidth: 180 }}
        placeholder="Select vendor"
        disabled={meta.poApproval}
        value={selectedBid?.vendorId ?? undefined}
        onChange={(vendorId) =>
          handleMaterialVendorSelect(record.materialKey, vendorId)
        }
        allowClear={!meta.poApproval}
        onClear={() => handleMaterialVendorSelect(record.materialKey, null)}
      >
        {material.vendorBids.map((vendorBid) => (
          <Option key={vendorBid.vendorId} value={vendorBid.vendorId}>
            {vendorBid.vendorName} — ₹{" "}
            {Number(vendorBid.total_amount_with_gst || 0).toLocaleString(
              "en-IN",
              { maximumFractionDigits: 0 },
            )}
          </Option>
        ))}
      </Select>
    );
  };

  const renderMaterialSelectReason = (_, record) => {
    if (record.rowType !== "material") {
      return null;
    }

    const material = findMaterialByRow(materials, record);
    if (!material) {
      return null;
    }

    const selectedBid = material.vendorBids.find(
      (vendorBid) => vendorBid.vendor_checked,
    );

    if (!selectedBid || !materialBidRequiresReason(selectedBid, material)) {
      return null;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Select
          style={{ width: "100%", minWidth: 180 }}
          showArrow
          placeholder="Select reason"
          disabled={meta.poApproval}
          value={selectedBid.select_reason}
          onChange={(value) =>
            handleReasonChange(
              value,
              selectedBid.vendorId,
              record.materialKey,
            )
          }
        >
          {REASONS.map((reason) => (
            <Option key={reason} value={reason}>
              {reason}
            </Option>
          ))}
        </Select>
        {selectedBid.select_reason === "Others" && meta.otherReason && (
          <Input
            maxLength={30}
            placeholder="Enter custom reason"
            disabled={meta.poApproval}
            value={
              otherReasons[`${selectedBid.vendorId}_${record.materialKey}`] ??
              ""
            }
            onChange={(e) =>
              handleOtherReasonChange(
                e.target.value,
                selectedBid.vendorId,
                record.materialKey,
              )
            }
          />
        )}
      </div>
    );
  };

  const handleReasonChange = (value, vendorId, materialKey = null) => {
    setMaterials((prev) =>
      prev.map((material) => {
        const rowKey = getGeneralMaterialRowKey(material);
        if (materialKey && rowKey !== materialKey) {
          return material;
        }

        return {
          ...material,
          vendorBids: material.vendorBids.map((vendorBid) => {
            if (vendorMode === "perItem") {
              return {
                ...vendorBid,
                select_reason:
                  sameVendorId(vendorBid.vendorId, vendorId) &&
                  vendorBid.vendor_checked
                    ? value
                    : vendorBid.select_reason,
              };
            }

            return {
              ...vendorBid,
              select_reason:
                vendorBid.vendorId === vendorId ? value : vendorBid.select_reason,
            };
          }),
        };
      }),
    );
  };

  const handleOtherReasonChange = (value, vendorId, materialKey = null) => {
    const reasonKey = materialKey
      ? `${vendorId}_${materialKey}`
      : `${vendorId}`;

    setOtherReasons((prev) => ({ ...prev, [reasonKey]: value }));
    setMaterials((prev) =>
      prev.map((material) => {
        const rowKey = getGeneralMaterialRowKey(material);
        if (materialKey && rowKey !== materialKey) {
          return material;
        }

        return {
          ...material,
          vendorBids: material.vendorBids.map((vendorBid) =>
            sameVendorId(vendorBid.vendorId, vendorId)
              ? { ...vendorBid, other_reason_text: value }
              : vendorBid,
          ),
        };
      }),
    );
  };

  const vendorRankingTotals = useMemo(() => {
    const findCheckedBidForVendor = (vendorId) =>
      materials
        .flatMap((material) => material.vendorBids)
        .find(
          (vendorBid) =>
            sameVendorId(vendorBid.vendorId, vendorId) && vendorBid.vendor_checked,
        );

    if (vendorGrandTotals.length > 0) {
      return vendorGrandTotals.map((vendorTotal) => {
        const checkedBid = findCheckedBidForVendor(vendorTotal.vendorId);

        return {
          vendorId: vendorTotal.vendorId,
          vendorName: vendorTotal.vendorName,
          totalAmount: vendorTotal.totalAmount,
          totalAmountWithGST: vendorTotal.totalAmountWithGST,
          selectedReason: checkedBid?.select_reason ?? null,
          vendorChecked: Boolean(checkedBid),
          other_reason_text: checkedBid?.other_reason_text ?? "",
          comparison_ranking: vendorTotal.comparison_ranking,
        };
      });
    }

    const totals = {};

    materials.forEach((material) => {
      material.vendorBids.forEach((vendorBid) => {
        if (!totals[vendorBid.vendorId]) {
          totals[vendorBid.vendorId] = {
            vendorId: vendorBid.vendorId,
            vendorName: vendorBid.vendorName,
            totalAmount: 0,
            totalAmountWithGST: 0,
            selectedReason: vendorBid.select_reason,
            vendorChecked: vendorBid.vendor_checked,
            other_reason_text: vendorBid.other_reason_text,
          };
        }

        totals[vendorBid.vendorId].totalAmount +=
          vendorBid.total_amount_without_gst || 0;
        totals[vendorBid.vendorId].totalAmountWithGST +=
          vendorBid.total_amount_with_gst || 0;

        if (vendorBid.vendor_checked) {
          totals[vendorBid.vendorId].vendorChecked = true;
          totals[vendorBid.vendorId].selectedReason = vendorBid.select_reason;
          totals[vendorBid.vendorId].other_reason_text =
            vendorBid.other_reason_text;
        }
      });
    });

    return Object.values(totals).sort(
      (a, b) => a.totalAmountWithGST - b.totalAmountWithGST
    );
  }, [materials, vendorGrandTotals]);

  const tableData = useMemo(() => {
    const rows = materials.map((material, index) => ({
      key: `material-${material.key}`,
      rowType: "material",
      materialKey: getGeneralMaterialRowKey(material),
      sr: index + 1,
      item: material.materialName,
      subCategory: material.subCategory,
      qty: Number(material.quantity || 0).toFixed(2),
      UOM: formatUom(material.uom),
      vendors: material.vendorBids.map((vendorBid) => ({
        vendorId: vendorBid.vendorId,
        vendorName: vendorBid.vendorName,
        unitRate: vendorBid.unitRate,
        totalAmount: vendorBid.total_amount_without_gst,
        totalAmountWithGST: vendorBid.total_amount_with_gst,
        selectedReason: vendorBid.select_reason,
        vendorChecked: vendorBid.vendor_checked,
        other_reason_text: vendorBid.other_reason_text,
      })),
    }));

    const grandTotalVendors =
      vendorGrandTotals.length > 0
        ? vendorGrandTotals.map((vendorTotal) => ({
            vendorName: vendorTotal.vendorName,
            unitRate: null,
            totalAmount: vendorTotal.totalAmount,
            totalAmountWithGST: vendorTotal.totalAmountWithGST,
          }))
        : vendorNames
            .map((vendorName) => {
              const totalAmount = rows.reduce((acc, row) => {
                const vendor = row.vendors.find((v) => v.vendorName === vendorName);
                return acc + (vendor?.totalAmount || 0);
              }, 0);
              const totalAmountWithGST = rows.reduce((acc, row) => {
                const vendor = row.vendors.find((v) => v.vendorName === vendorName);
                return acc + (vendor?.totalAmountWithGST || 0);
              }, 0);

              if (totalAmount <= 0) return null;

              return {
                vendorName,
                unitRate: null,
                totalAmount,
                totalAmountWithGST,
              };
            })
            .filter(Boolean);

    if (grandTotalVendors.length > 0) {
      rows.push({
        key: "grand-total",
        rowType: "grand-total",
        sr: null,
        item: null,
        subCategory: null,
        qty: null,
        UOM: null,
        vendors: grandTotalVendors,
      });
    }

    if (vendorRankingTotals.length > 0) {
      rows.push({
        key: "ranking",
        rowType: "ranking",
        sr: null,
        item: null,
        subCategory: null,
        qty: null,
        UOM: null,
        vendors: vendorRankingTotals.map((vendor, rankIndex) => ({
          vendorId: vendor.vendorId,
          vendorName: vendor.vendorName,
          unitRate: null,
          totalAmount: vendor.totalAmount,
          totalAmountWithGST: vendor.totalAmountWithGST,
          selectedReason: vendor.selectedReason,
          vendorChecked: vendor.vendorChecked,
          other_reason_text: vendor.other_reason_text,
          rank: vendor.comparison_ranking ?? `L${rankIndex + 1}`,
        })),
      });
    }

    return rows;
  }, [materials, vendorGrandTotals, vendorNames, vendorRankingTotals]);

  const vendorColumns = vendorNames.flatMap((vendorName) => ({
    title: vendorName,
    width: 150,
    className: "thirdth",
    children: [
      {
        title: "Unit Rate",
        dataIndex: "vendors",
        width: 70,
        align: "right",
        render: (vendors, row) => {
          if (!Array.isArray(vendors)) return null;
          const vendor = vendors.find((v) => v.vendorName === vendorName);
          if (!vendor) return null;

          if (row.rowType === "ranking") {
            const vendorTotals = row.vendors.map((v) => v.totalAmountWithGST ?? 0);
            const minTotal = Math.min(...vendorTotals);
            const secondMinTotal = Math.min(
              ...vendorTotals.filter((total) => total > minTotal),
            );
            const thirdMinTotal = Math.min(
              ...vendorTotals.filter((total) => total > secondMinTotal),
            );
            const totalAmount = vendor.totalAmountWithGST ?? 0;
            const rankLabel = vendor.rank;
            const showL1 = rankLabel === "L1" || (!rankLabel && totalAmount === minTotal);
            const showL2 =
              rankLabel === "L2" ||
              (!rankLabel && totalAmount === secondMinTotal);
            const showL3 =
              rankLabel === "L3" ||
              (!rankLabel && totalAmount === thirdMinTotal);
            const isChecked = sameVendorId(vendor.vendorId, selectedVendorId);
            const isDisabled = selectedVendorId && !isChecked;
            const showCommonSelection = vendorMode === "common";

            return {
              children: (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "8px",
                    paddingRight: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      justifyContent: "flex-end",
                    }}
                  >
                    {showL1 && (
                      <Tooltip
                        color="#094E8F"
                        placement="left"
                        title={<span style={{ color: "#fff" }}>Lowest Price</span>}
                      >
                        <span className="lowest2">L1</span>
                      </Tooltip>
                    )}
                    {showL2 && (
                      <Tooltip
                        color="#094E8F"
                        placement="left"
                        title={
                          <span style={{ color: "#fff" }}>
                            Second Lowest Price
                          </span>
                        }
                      >
                        <span className="secondLowest2">L2</span>
                      </Tooltip>
                    )}
                    {showL3 && (
                      <Tooltip
                        color="#094E8F"
                        placement="left"
                        title={
                          <span style={{ color: "#fff" }}>
                            Third Lowest Price
                          </span>
                        }
                      >
                        <span className="thirdLowest2">L3</span>
                      </Tooltip>
                    )}
                    {showCommonSelection && (
                      <Checkbox
                        checked={isChecked}
                        disabled={meta.poApproval || isDisabled}
                        onChange={(e) =>
                          handleVendorSelect(vendor.vendorId, e.target.checked)
                        }
                      />
                    )}
                  </div>
                  {showCommonSelection &&
                    (showL1 ? (
                      <Select
                        style={{ width: "200px", visibility: "hidden" }}
                        showArrow
                        placeholder="Select Reason"
                      />
                    ) : (
                      <Select
                        style={{ width: "200px" }}
                        showArrow
                        placeholder="Select Reason"
                        disabled={
                          meta.poApproval || (!isChecked && !vendor.selectedReason)
                        }
                        value={vendor.selectedReason}
                        onChange={(value) =>
                          handleReasonChange(value, vendor.vendorId)
                        }
                      >
                        {REASONS.map((reason) => (
                          <Option key={reason} value={reason}>
                            {reason}
                          </Option>
                        ))}
                      </Select>
                    ))}
                  {showCommonSelection &&
                    vendor.selectedReason === "Others" &&
                    meta.otherReason && (
                      <Input
                        style={{ marginTop: 8, width: 200 }}
                        maxLength={30}
                        placeholder="Enter custom reason"
                        value={otherReasons[vendor.vendorId] ?? ""}
                        onChange={(e) =>
                          handleOtherReasonChange(e.target.value, vendor.vendorId)
                        }
                      />
                    )}
                </div>
              ),
              props: { colSpan: 3 },
            };
          }

          if (row.rowType === "grand-total" || vendor.unitRate == null) {
            return null;
          }

          const selectedForRow = getSelectedVendorIdForRow(row);
          const isHighlighted =
            selectedForRow != null &&
            sameVendorId(vendor.vendorId, selectedForRow);

          return (
            <div
              style={
                isHighlighted
                  ? {
                      background: "#e8f4fc",
                      borderRadius: 4,
                      padding: "4px 6px",
                      display: "inline-block",
                    }
                  : undefined
              }
            >
              <RupeesSign style={{ height: "10px", marginTop: "3px" }} />{" "}
              {Number(vendor.unitRate || 0).toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
            </div>
          );
        },
      },
      {
        title: (
          <>
            <span>Total Amount</span>
            <br />
            <span>Without GST</span>
          </>
        ),
        dataIndex: "vendors",
        width: 100,
        align: "right",
        render: (vendors, row) => {
          if (!Array.isArray(vendors)) return null;
          const vendor = vendors.find((v) => v.vendorName === vendorName);
          if (!vendor) return null;
          if (row.rowType === "ranking") {
            return { props: { colSpan: 0 } };
          }

          const selectedForRow = getSelectedVendorIdForRow(row);
          const isHighlighted =
            selectedForRow != null &&
            sameVendorId(vendor.vendorId, selectedForRow);
          const className =
            row.rowType === "grand-total" ? "subtotall" : "normal-field";

          return (
            <div
              className={className}
              style={
                isHighlighted
                  ? { background: "#e8f4fc", borderRadius: 4, padding: "4px 6px" }
                  : undefined
              }
            >
              ₹{" "}
              {Number(vendor.totalAmount || 0).toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
            </div>
          );
        },
      },
      {
        title: (
          <>
            <span>Total Amount</span>
            <br />
            <span>With GST</span>
          </>
        ),
        dataIndex: "vendors",
        width: 80,
        align: "right",
        render: (vendors, row) => {
          if (!Array.isArray(vendors)) return null;
          const vendor = vendors.find((v) => v.vendorName === vendorName);
          if (!vendor) return null;
          if (row.rowType === "ranking") {
            return { props: { colSpan: 0 } };
          }

          const selectedForRow = getSelectedVendorIdForRow(row);
          const isHighlighted =
            selectedForRow != null &&
            sameVendorId(vendor.vendorId, selectedForRow);
          const className =
            row.rowType === "grand-total" ? "subtotall" : "normal-field";

          return (
            <div
              className={className}
              style={
                isHighlighted
                  ? { background: "#e8f4fc", borderRadius: 4, padding: "4px 6px" }
                  : undefined
              }
            >
              ₹{" "}
              {Number(vendor.totalAmountWithGST || 0).toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
            </div>
          );
        },
      },
    ],
  }));

  const columns = [
    {
      title: "Sr.",
      dataIndex: "sr",
      width: 30,
      render: (text, record) =>
        record.rowType === "material" ? text : null,
    },
    {
      title: "Items",
      dataIndex: "item",
      width: 120,
      render: (text, record) => {
        if (record.rowType === "grand-total") {
          return <div className="subtotal">Grand Total</div>;
        }
        if (record.rowType === "ranking") {
          return <div className="subtotal">Ranking</div>;
        }
        return (
          <div>
            <div>{record.item}</div>
            {record.subCategory ? (
              <div style={{ fontSize: "12px", color: "#666" }}>
                {record.subCategory}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Qty.",
      dataIndex: "qty",
      width: 80,
      align: "center",
    },
    {
      title: "UoM.",
      dataIndex: "UOM",
      width: 80,
      align: "center",
    },
    ...(vendorMode === "perItem"
      ? [
          {
            title: "Vendor Selection",
            key: "vendorSelection",
            width: 220,
            render: renderMaterialVendorSelection,
          },
          {
            title: "Select Reason",
            key: "selectReason",
            width: 220,
            render: renderMaterialSelectReason,
          },
        ]
      : []),
    ...vendorColumns,
  ];

  const handleSubmit = async () => {
    if (vendorMode === "common") {
      if (!selectedVendorId) {
        ErrorNotificationMsg("Please select vendor");
        return;
      }

      const selectedVendorRanking = vendorRankingTotals.find(
        (vendor) => vendor.vendorId === selectedVendorId,
      );
      const isL1 =
        selectedVendorRanking?.comparison_ranking === "L1" ||
        vendorRankingTotals[0]?.vendorId === selectedVendorId;

      const selectedReason = materials
        .flatMap((material) => material.vendorBids)
        .find((vendorBid) => vendorBid.vendorId === selectedVendorId)
        ?.select_reason;

      if (!isL1 && !selectedReason) {
        ErrorNotificationMsg("Please select a reason");
        return;
      }
    } else {
      const unselectedMaterial = materials.find(
        (material) => !material.vendorBids.some((vendorBid) => vendorBid.vendor_checked),
      );

      if (unselectedMaterial) {
        ErrorNotificationMsg(
          `Please select vendor for ${unselectedMaterial.materialName}`,
        );
        return;
      }

      const materialMissingReason = materials.find((material) => {
        const selectedBid = material.vendorBids.find(
          (vendorBid) => vendorBid.vendor_checked,
        );
        if (!selectedBid) return false;

        return (
          materialBidRequiresReason(selectedBid, material) &&
          !selectedBid.select_reason
        );
      });

      if (materialMissingReason) {
        ErrorNotificationMsg(
          `Please select a reason for ${materialMissingReason.materialName}`,
        );
        return;
      }
    }

    const payload = buildGeneralPoOrderSubmitPayload({
      company: meta.company,
      generalRfq: meta.floatId,
      vendorMode,
      selectedVendorId,
      materials,
      vendorGrandTotals,
    });

    try {
      setBtnLoading(true);
      const response = await postRequest("general-po-order/", payload);
      if (response?.data?.success) {
        SuccessNotificationMsg(
          response?.data?.message || "Submitted successfully",
        );
        navigate(`/general-procurement/previewandsendapproval/${id}`);
      }
    } catch (error) {
      console.error("Summary comparative submit payload:", payload, error);
      ErrorNotificationMsg("Failed to submit price comparative review");
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>Summary Of Price Comparative Statement</h3>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/general-procurement-list">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to={`/general-procurement/comparative/${id}`}>
                  General Procurement Comparative
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                Summary Of Price Comparative Statement
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
      </div>

      <div className="stepscontentwrap">
        <div className="stepsContent">
          <section className="grid-sec mt-5">
            <div className="PR-body" style={{ padding: "0" }}>
              <h5 className="rfq-number new">
                RFQ Number : <span>{meta.rfqNumber}</span>
              </h5>

              {!meta.poApproval && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    flexWrap: "wrap",
                    margin: "16px 0",
                  }}
                >
                  <div style={{ fontWeight: 600, color: "#111827", fontSize: 15 }}>
                    Vendor selection
                  </div>
                  <Radio.Group
                    value={vendorMode}
                    onChange={(e) => handleVendorModeChange(e.target.value)}
                  >
                    <Radio value="common">Same vendor for all materials</Radio>
                    <Radio value="perItem">Different vendor per material</Radio>
                  </Radio.Group>
                  {vendorMode === "perItem" && (
                    <span style={{ color: "#6b7280", fontSize: 13 }}>
                      Use the Vendor Selection column to assign a vendor per
                      material. Matching prices are highlighted in the table.
                    </span>
                  )}
                </div>
              )}

              <Spin spinning={apiLoading}>
                <Table
                  dataSource={tableData}
                  className="comparativecustomtbl"
                  bordered
                  scroll={{ x: "auto" }}
                  pagination={false}
                  columns={columns}
                />
                <div className="TotalAMT">
                  <label>Proposed Order Value: </label>
                  <div
                    className="citytotal"
                    style={{ width: "140px", textAlign: "end" }}
                  >
                    ₹{" "}
                    {Number(selectedVendorTotalAmount || 0).toLocaleString(
                      "en-IN",
                      { maximumFractionDigits: 0 }
                    )}
                  </div>
                </div>
              </Spin>
            </div>
          </section>
        </div>

        <div className="FormFooter bidftr" style={{ padding: "16px", margin: "0" }}>
          <ButtonField
            className="SaveGradiantBtn"
            type="primary"
            label="Previous"
            onClick={() => navigate(`/general-procurement/comparative/${id}`)}
          />
          {!meta.poApproval && (
            <ButtonField
              className="SaveGradiantBtn"
              type="primary"
              label="Preview and Send for PO Approval"
              onClick={handleSubmit}
              loading={btnLoading}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default GeneralProcurementSummaryPriceComparative;
